import React from 'react';
import { animateColors } from '../../../constants/components/tailwindClassCollections';
import { useClassyBoy } from '../../../hooks/useClassyBoy';
import { Brands } from '../../../models/brands';
import LoadingSpinner from '../misc/LoadingSpinner';
import { BrandStyling } from '../../../theme/brandStyling';

type AppButtonProps = {
    className?: string;
    sizeClassName?: string;
    isEnabled?: boolean;
    isLoading?: boolean;
    mode?:
        | 'primary'
        | 'secondary'
        | 'errorPrimary'
        | 'errorSecondary'
        | 'successPrimary'
        | 'successSecondary';
    onClick?: Function;
    children?: React.ReactNode;
    branding?: Brands;
} & React.ButtonHTMLAttributes<HTMLButtonElement>;

const primaryModes = ['primary', 'errorPrimary', 'successPrimary'];

const AppButton: React.FC<AppButtonProps> = ({
    type,
    value,
    children,
    className,
    sizeClassName,
    isEnabled,
    isLoading,
    mode = 'primary',
    onClick,
    branding = Brands.URocked,
}) => {
    const baseClasses = useClassyBoy(
        'rounded-full cursor-pointer text-sm',
        animateColors,
        className,
    );

    const modeClasses = useClassyBoy();
    const combinedClasses = useClassyBoy();

    baseClasses.add(sizeClassName ?? 'w-24 h-8');
    const styling = BrandStyling.byBrand(branding);
    const style = {
        disabledPrimary: `pointer-events-none ${styling.buttonPrimaryDisabled}`,
        disabledSecondary:
            'border-disabled-1 text-disabled-1 pointer-events-none',
        primary: `text-white ${styling.buttonPrimary}`,
        secondary: `border-2 bg-transparent ${styling.buttonSecondary ? styling.buttonSecondary : 'border-primary-1 text-primary-1'}`,
        errorPrimary: 'bg-error text-white',
        errorSecondary:'border-2 border-error bg-transparent text-error hover:bg-primary-1 hover:border-transparent hover:text-white',
        successPrimary:
            'bg-success-1 text-white hover:bg-success-2 hover:text-success-1',
        successSecondary:
            'border-2 border-success-1 bg-transparent text-success-1 hover:bg-success-1 hover:border-transparent hover:text-white ',
        loading: 'pointer-events-none',
    };

    if (isEnabled !== undefined && !isEnabled) {
        if (primaryModes.includes(mode)) {
            modeClasses.set(style.disabledPrimary);
        } else {
            modeClasses.set(style.disabledSecondary);
        }
    } else if (isLoading !== undefined && isLoading) {
        modeClasses.set(style.loading);
    } else {
        modeClasses.set(style[mode]);
    }

    combinedClasses.set(baseClasses.result, modeClasses.result);

    return (
        <button
            type={type}
            className={combinedClasses.result}
            onClick={onClick}
        >
            {isLoading ? <LoadingSpinner /> : children ?? value}
        </button>
    );
};

export default AppButton;
